import React, { useState } from 'react';
import { Plus, Calendar, TrendingUp, X } from 'lucide-react';
import { goals } from '../../../data/mockData';
import type { Goal } from '../../../data/mockData';
function CircularProgress({
  percent,
  color,
  size = 100
}: {
  percent: number;
  color: string;
  size?: number;
}) {
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - percent / 100 * circ;
  return <svg width={size} height={size} className="-rotate-90" data-id="element-350">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={8} className="dark:stroke-gray-700" data-id="element-351" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={8} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{
      transition: 'stroke-dashoffset 1s ease'
    }} data-id="element-352" />
    </svg>;
}
function GoalCard({
  goal
}: {
  goal: Goal;
}) {
  const pct = Math.round(goal.currentAmount / goal.targetAmount * 100);
  const remaining = goal.targetAmount - goal.currentAmount;
  const deadline = new Date(goal.deadline);
  const today = new Date();
  const daysLeft = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const monthsLeft = Math.ceil(daysLeft / 30);
  const projectedCompletion = monthsLeft > 0 ? Math.round(remaining / goal.monthlyContribution) : 0;
  const onTrack = projectedCompletion <= monthsLeft;
  return <div className="bg-white dark:bg-dark-card rounded-2xl p-6 border border-gray-100 dark:border-dark-border shadow-sm hover:shadow-md transition-shadow" data-id="element-353">
      <div className="flex items-start justify-between mb-4" data-id="element-354">
        <div data-id="element-355">
          <div className="flex items-center gap-2 mb-1" data-id="element-356">
            <span className="text-2xl" data-id="element-357">{goal.emoji}</span>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white" data-id="element-358">
              {goal.name}
            </h3>
          </div>
          <div className="flex items-center gap-2" data-id="element-359">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${onTrack ? 'bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-400' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'}`} data-id="element-360">
              {onTrack ? '✓ On track' : '⚠ Needs attention'}
            </span>
          </div>
        </div>
        <div className="relative flex items-center justify-center" data-id="element-361">
          <CircularProgress percent={pct} color={goal.color} size={80} data-id="element-362" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-363">
            <span className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-364">
              {pct}%
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-3" data-id="element-365">
        <div className="flex justify-between text-sm" data-id="element-366">
          <span className="text-gray-500 dark:text-gray-400" data-id="element-367">Progress</span>
          <span className="font-semibold text-gray-800 dark:text-gray-200" data-id="element-368">
            ${goal.currentAmount.toLocaleString()}{' '}
            <span className="text-gray-400 font-normal" data-id="element-369">
              / ${goal.targetAmount.toLocaleString()}
            </span>
          </span>
        </div>
        <div className="h-2 bg-gray-100 dark:bg-dark-hover rounded-full overflow-hidden" data-id="element-370">
          <div className="h-full rounded-full transition-all duration-700" style={{
          width: `${pct}%`,
          backgroundColor: goal.color
        }} data-id="element-371" />
        </div>
        <div className="grid grid-cols-2 gap-3 pt-1" data-id="element-372">
          <div className="bg-gray-50 dark:bg-dark-hover rounded-xl p-3" data-id="element-373">
            <p className="text-xs text-gray-400 mb-0.5" data-id="element-374">Remaining</p>
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200" data-id="element-375">
              ${remaining.toLocaleString()}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-dark-hover rounded-xl p-3" data-id="element-376">
            <p className="text-xs text-gray-400 mb-0.5" data-id="element-377">Monthly</p>
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200" data-id="element-378">
              +${goal.monthlyContribution}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between pt-1" data-id="element-379">
          <div className="flex items-center gap-1.5 text-xs text-gray-400" data-id="element-380">
            <Calendar size={12} data-id="element-381" />
            <span data-id="element-382">
              Due{' '}
              {deadline.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
            </span>
          </div>
          <span className="text-xs text-gray-400" data-id="element-383">
            {daysLeft > 0 ? `${daysLeft} days left` : 'Overdue'}
          </span>
        </div>
      </div>

      <button className="w-full mt-4 py-2 rounded-xl text-sm font-medium transition-colors border-2" style={{
      borderColor: goal.color,
      color: goal.color
    }} onMouseEnter={e => {
      ;
      (e.target as HTMLButtonElement).style.backgroundColor = goal.color;
      (e.target as HTMLButtonElement).style.color = 'white';
    }} onMouseLeave={e => {
      ;
      (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
      (e.target as HTMLButtonElement).style.color = goal.color;
    }} data-id="element-384">
        Add Funds
      </button>
    </div>;
}
export function GoalsView() {
  const totalTarget = goals.reduce((s, g) => s + g.targetAmount, 0);
  const totalCurrent = goals.reduce((s, g) => s + g.currentAmount, 0);
  const overallPct = Math.round(totalCurrent / totalTarget * 100);
  return <div className="space-y-6" data-id="element-385">
      {/* Summary */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl p-6 text-white" data-id="element-386">
        <div className="flex items-center justify-between" data-id="element-387">
          <div data-id="element-388">
            <p className="text-primary-200 text-sm mb-1" data-id="element-389">Overall Progress</p>
            <p className="text-4xl font-black" data-id="element-390">{overallPct}%</p>
            <p className="text-primary-200 text-sm mt-1" data-id="element-391">
              ${totalCurrent.toLocaleString()} saved of $
              {totalTarget.toLocaleString()} goal
            </p>
          </div>
          <div className="relative" data-id="element-392">
            <CircularProgress percent={overallPct} color="#39E75F" size={100} data-id="element-393" />
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-394">
              <TrendingUp size={24} className="text-accent-400" data-id="element-395" />
            </div>
          </div>
        </div>
        <div className="mt-4 h-2 bg-primary-400/30 rounded-full overflow-hidden" data-id="element-396">
          <div className="h-full bg-accent-400 rounded-full transition-all duration-700" style={{
          width: `${overallPct}%`
        }} data-id="element-397" />
        </div>
      </div>

      {/* Goals grid */}
      <div className="flex items-center justify-between" data-id="element-398">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-399">
          {goals.length} Active Goals
        </h3>
        <button className="flex items-center gap-2 text-sm bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl transition-colors font-medium" data-id="element-400">
          <Plus size={15} data-id="element-401" /> New Goal
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-402">
        {goals.map(goal => <GoalCard key={goal.id} goal={goal} data-id="element-403" />)}

        {/* Add goal card */}
        <div className="bg-gray-50 dark:bg-dark-hover rounded-2xl border-2 border-dashed border-gray-200 dark:border-dark-border flex flex-col items-center justify-center p-8 cursor-pointer hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors group" data-id="element-404">
          <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-dark-border group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 flex items-center justify-center mb-3 transition-colors" data-id="element-405">
            <Plus size={20} className="text-gray-400 group-hover:text-primary-500 transition-colors" data-id="element-406" />
          </div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" data-id="element-407">
            Create New Goal
          </p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-408">
            Set a target and track progress
          </p>
        </div>
      </div>
    </div>;
}